import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import moment from "moment";
import { Select } from "components/Form";
import { VerticalBar } from "components/Charts";
import { DAYS_OF_WEEK } from "constants/index";
import { colors } from "style";

const DAY_NAMES = DAYS_OF_WEEK.map((day) => day.label.slice(0, 3));

/**
 * TimeSheetChart
 *
 * @param {Object} data - The timesheet data to be displayed in the chart.
 * @param {string} week - The selected week for the timesheet.
 * @param {function} onWeekChange - Callback function to handle week change.
 */
const TimeSheetChart = ({ data, week, onWeekChange, ...props }) => {
  const weekOptions = useMemo(() => generatePastWeekOptions(), []);
  const chartData = useMemo(
    () => ({
      data: DAY_NAMES.map((day, index) => {
        const dayData = data?.find((d) => d.dayIndex === index);
        return {
          day,
          hours: dayData?.hoursLogged || 0.0,
        };
      }),
      seriesName: "Hours Logged",
      barColor: colors.purpleRainBase,
    }),
    [data]
  );

  // EmptyDataCase: Check if all hours are zero and set data to null
  if (chartData?.data && !chartData.data.filter((item) => item.hours > 0).length) {
    chartData.data = null;
  }

  const handleWeekChange = (event, value) => {
    onWeekChange(value);
  };

  const noResultConfigs = {
    title: "Missions.TimeSheets.TeamTimeSheetSummary.NoDataTitle",
    description: null,
  };

  return (
    <div>
      {weekOptions.length > 0 && (
        <WeekSelector>
          <Select
            name="week"
            onChange={handleWeekChange}
            options={weekOptions}
            required={true}
            defaultValue={weekOptions[0]?.value}
            value={week}
          />
        </WeekSelector>
      )}
      <VerticalBar data={chartData} noResultConfigs={noResultConfigs} {...props} />
    </div>
  );
};

const generatePastWeekOptions = () => {
  const options = [];

  for (let i = 0; i < 4; i++) {
    const weekMoment = moment().subtract(i, "weeks");
    const startOfWeek = weekMoment.clone().startOf("isoWeek");
    const endOfWeek = weekMoment.clone().endOf("isoWeek");

    options.push({
      value: weekMoment.isoWeek(), // ISO week number
      label: `Week of ${startOfWeek.format("MMM D")} - ${endOfWeek.format("MMM D")}`,
    });
  }

  return options;
};

const WeekSelector = styled.div`
  margin-bottom: 1.6rem;
`;

TimeSheetChart.defaultProps = {
  onWeekChange: () => {},
};

TimeSheetChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      dayIndex: PropTypes.number,
      hoursLogged: PropTypes.number,
    })
  ),
  week: PropTypes.string.isRequired,
  onWeekChange: PropTypes.func,
};

export default TimeSheetChart;
